import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SharedService } from './sharedService';
import { ProjectService } from './project.service';
import { MessagingServiceService } from './messaging-service.service';
import { MatDialog } from '@angular/material/dialog';
import { WelcomeComponent } from '../welcome/welcome.component';
import Swal from 'sweetalert2';
import { CacheService } from './cache.service';
import { GoogleAuthService } from './google-auth.service';
import { ProfileCompletionService } from './profile-completion.service';
import { DeviceDetectionService } from './device-detection.service';
import { PhoneVerificationService } from './phone-verification.service';
import { TokenEncryptionService } from './token-encryption.service';
import { clearStoragePreservingAttribution } from '../utils/attribution.util';
import { ActivationDestinationService } from '../onboarding-checklist/activation-destination.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    private router: Router,
    private sharedService: SharedService,
    private projectService: ProjectService,
    private messagingService: MessagingServiceService,
    private dialog: MatDialog,
    private cacheService: CacheService,
    private googleAuthService: GoogleAuthService,
    private profileCompletionService: ProfileCompletionService,
    private deviceDetectionService: DeviceDetectionService,
    private phoneVerificationService: PhoneVerificationService,
    private tokenEncryptionService: TokenEncryptionService,
    private activationDestination: ActivationDestinationService
  ) { }

  /**
   * Handles common tasks after successful login
   * @param userData User data from login response
   * @param isGoogleLogin Whether this is a Google login
   * @param isNewLogin Whether this is a fresh login action (e.g., user just clicked login button)
   */
  handleSuccessfulLogin(userData: any, isGoogleLogin: boolean = false, isNewLogin: boolean = true): void {
    // Reset skip timer on every login
    this.profileCompletionService.clearSkipData();

    // Detect and store device type
    const deviceType = this.deviceDetectionService.detectDeviceType();
    this.deviceDetectionService.storeDeviceType();
    console.log(`[Auth] User logged in from ${deviceType} device`);

    // Store token securely (encrypted) and mark session as logged in
    const authToken = userData.token;
    this.tokenEncryptionService.setToken(authToken);
    localStorage.setItem('isLogedIn', 'true');
    // Store a sanitized version of the login payload without the raw token
    const { token, ...sanitizedUserData } = userData || {};
    this.tokenEncryptionService.setEncryptedItem('logedInUser', sanitizedUserData);
    // Also store encrypted user payload for sensitive data
    this.tokenEncryptionService.setUserData(userData);
    // Set or remove flag for Google login
    if (isGoogleLogin) {
      localStorage.setItem('isGoogleLogin', 'true');
    } else {
      localStorage.removeItem('isGoogleLogin'); // Ensure this is removed for non-Google logins
    }

    this.projectService.getUser().subscribe((res) => {
      const isOnBoarding = this.sharedService.checkingOnBoarding(res);
      if (!isOnBoarding) {
        if (isNewLogin) {
          Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Successful login',
            timer: 1500,
            showConfirmButton: false,
          });
        }
        this.router.navigate(['/setup/ready']);
      } else {
        this.sharedService.logInStateSubject.next(true);
        this.sharedService.sideMenuSubject.next(false);
        this.sharedService.isOpenCloseSidebar.next(true);
        if (isNewLogin) {
          Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Successful login',
            timer: 1500,
            showConfirmButton: false,
          });
        }
        this.activationDestination.postLoginDestination().subscribe((dest) => {
          this.router.navigate([dest.route], {
            queryParams: dest.queryParams || {},
          });
        });
      }
    });
    this.setupDatabasePath(userData);
  }

  /**
   * Sets up database path and related services
   * @param userData User data from login response
   */
  private setupDatabasePath(userData: any): void {
    // Get user ID from appropriate location in response - prioritize company_id for login responses
    let userId = userData.company_id || userData.user?.company_id;

    if (!userId) {
      // If no company_id, try to get user ID from other sources
      if (!userId || userId.includes('@')) {
        userId = userData.user?.id || userData.id;
      }

      // If userId is still an email, try to get a numeric ID from other sources
      if (!userId || userId.includes('@')) {
        // Try to get numeric ID from user object
        userId = userData.user?.user_id || userData.user_id || userData.user?.ID || userData.ID;
      }

      // If still no proper ID, use company_id but ensure it's not an email
      if (!userId || userId.includes('@')) {
        userId = userData.user?.company_id || userData.company_id;

        // If company_id is also an email, we need to handle this case
        if (userId && userId.includes('@')) {
          console.warn('Both user ID and company ID are emails, this may cause Firebase path issues');
          // For now, create a safe hash from email
          userId = this.createSafeFirebasePath(userId);
        }
      }
    }


    // Fallback to a safe default if no valid ID found
    if (!userId) {
      userId = 'default';
      console.warn('No valid user ID found, using default path');
    }

    const dbPath = `/users/${userId}`;

    console.log('Setting up database path:', {
      originalUserId: userData.user?.id || userData.id,
      originalCompanyId: userData.company_id || userData.user?.company_id,
      finalUserId: userId,
      dbPath: dbPath
    });

    // Store path in encrypted localStorage and services
    this.tokenEncryptionService.setEncryptedItem('dbPath', dbPath);
    this.messagingService.dbPath = dbPath;
    this.projectService.updateFirebaseData({ data: dbPath });
    this.projectService.updateEmployeeListData({ data: true });
    this.messagingService.resetData();

    // Fetch additional user data
    this.fetchUserDataAndSetup(userData);
  }

  /**
   * Create a safe Firebase path from an email address
   */
  private createSafeFirebasePath(email: string): string {
    // Remove special characters and create a safe identifier
    return email
      .replace(/[.#$\[\]@]/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .substring(0, 50); // Limit length for Firebase
  }


  /**
   * Fetches additional user data and sets up welcome dialog and messaging
   * @param userData User data from login response
   */
  public fetchUserDataAndSetup(userData: any): void {
    this.projectService.getUser().subscribe((user: any) => {
      // Store user data
      this.tokenEncryptionService.setEncryptedItem('user', user);

      // Set up messaging
      const userId = user.id || userData.id;
      this.setupMessaging(userId);
    });
  }

  /**
   * Sets up messaging service
   * @param userId User ID
   */
  private setupMessaging(userId: string): void {
    this.messagingService.requestPermission(userId);
    this.messagingService.receiveMessage();
    this.messagingService.showNotification();
  }

  private _clearAppSessionAndRedirect(): void {
    // Common items to remove for any logout
    this.tokenEncryptionService.clearAll();
    localStorage.removeItem('token');
    localStorage.removeItem('isLogedIn');
    localStorage.removeItem('logedInUser');
    this.tokenEncryptionService.clearEncryptedItem('user');
    localStorage.removeItem('dbPath');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('tourStart');
    localStorage.removeItem('isGoogleLogin'); // Ensure this is always removed here
    localStorage.removeItem('isTour');
    localStorage.removeItem('tourSkipped');
    localStorage.removeItem('deviceType'); // Clear device type
    // Clear dialog suppression preferences
    localStorage.removeItem('skipAdminControlConfirm');
    localStorage.removeItem('skipAdminControlConfirmDownload');
    localStorage.removeItem('globalSettings');
    sessionStorage.clear(); // Clear session storage
    localStorage.removeItem('reportRecipientEmails');
    localStorage.removeItem('professional-plan-announcement-seen'); // Clear announcement dialog flag
    localStorage.removeItem('supersee_demo_dashboard_opt_in');
    localStorage.removeItem('sampleDataBannerDismissed');
    clearStoragePreservingAttribution(localStorage);
    this.sharedService.isOpenCloseSidebar.next(true);
    // Clear phone verification status
    this.phoneVerificationService.clearVerificationStatus();

    // Set a flag indicating a manual logout is in progress before reloading
    sessionStorage.setItem('manual_logout_in_progress', 'true');

    // Clear caches and update shared services
    this.cacheService.clear();
    this.projectService.clearUserListCache();
    this.sharedService.logInStateSubject.next(false);
    this.messagingService.resetData();

    // Navigate to login page
    this.router.navigate(["/login"]).then(() => {
      window.location.reload(); // Force reload from server
    });

    console.log('Application session cleared and redirected to login.');
  }

  public performLogout(): void {
    const isGoogleLogin = localStorage.getItem('isGoogleLogin') === 'true';

    if (isGoogleLogin) {
      this.googleAuthService.signOut()
        .then(() => {
          console.log('Firebase signout successful');
          this._clearAppSessionAndRedirect();
        })
        .catch(error => {
          console.error('Firebase signout error:', error);
          // Proceed with app logout even if Firebase signout fails
          this._clearAppSessionAndRedirect();
        });
    } else {
      // Regular logout
      this._clearAppSessionAndRedirect();
    }
  }
}
